import React, { ChangeEvent, DragEvent, useMemo, useRef, useState } from "react";
import { mdiAlertCircleOutline, mdiCloudUploadOutline } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import styles from "./style.module.scss";

interface ReceivablesDropzoneProps {
  onFileSelect: (file: File) => void;
}

const ACCEPTED_FILE_EXTENSIONS = [".csv", ".xls", ".xlsx"];
const ACCEPTED_FILE_TYPES = [
  "text/csv",
  "application/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
];
const ACCEPTED_FILE_INPUT = [...ACCEPTED_FILE_EXTENSIONS, ...ACCEPTED_FILE_TYPES].join(",");

const isAcceptedFile = (file: File) => {
  if (file.type) {
    return ACCEPTED_FILE_TYPES.includes(file.type);
  }

  const fileName = file.name.toLowerCase();
  return ACCEPTED_FILE_EXTENSIONS.some((extension) => fileName.endsWith(extension));
};

export default function ReceivablesDropzone({ onFileSelect }: ReceivablesDropzoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDragActive, setIsDragActive] = useState(false);
  const [isDragReject, setIsDragReject] = useState(false);

  const handleFile = (file?: File) => {
    if (!file) {
      return;
    }
    if (!isAcceptedFile(file)) {
      setErrorMessage(`${file.name}: Use a CSV, XLS, or XLSX file.`);
      return;
    }
    setErrorMessage("");
    onFileSelect(file);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleFile(event.target.files?.[0]);
    event.target.value = "";
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.items?.[0];
    const fileType = file?.type ?? "";
    setIsDragActive(true);
    setIsDragReject(Boolean(fileType) && !ACCEPTED_FILE_TYPES.includes(fileType));
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);
    setIsDragReject(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);
    setIsDragReject(false);
    handleFile(event.dataTransfer.files?.[0]);
    event.dataTransfer.clearData();
  };

  const helperText = useMemo(() => {
    if (isDragReject) {
      return "Use a CSV, XLS, or XLSX file.";
    }
    if (isDragActive) {
      return "Drop the receivables file here.";
    }
    return "Drop a CSV or Excel file here.";
  }, [isDragActive, isDragReject]);

  return (
    <div
      role="button"
      tabIndex={0}
      className={clsx(
        styles.dropzone,
        isDragActive && styles.dropzoneActive,
        isDragReject && styles.dropzoneReject
      )}
      onClick={() => fileInputRef.current?.click()}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          fileInputRef.current?.click();
        }
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FILE_INPUT}
        onChange={handleFileChange}
        hidden
      />
      <span className={styles.dropzoneIcon}>
        <Icon path={mdiCloudUploadOutline} size={1.2} />
      </span>
      <div className={styles.dropzoneText}>
        <span className={styles.dropzoneTitle}>Upload File</span>
        <span className={styles.dropzoneHelper}>{helperText}</span>
        <span className={styles.dropzoneMeta}>CSV, XLS, XLSX</span>
      </div>
      <button
        type="button"
        className={styles.dropzoneButton}
        onClick={(event) => {
          event.stopPropagation();
          fileInputRef.current?.click();
        }}>
        Browse
      </button>
      {errorMessage ? (
        <span className={styles.dropzoneError}>
          <Icon path={mdiAlertCircleOutline} size={0.75} />
          {errorMessage}
        </span>
      ) : null}
    </div>
  );
}
